import {
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  TextArea,
} from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { ResidentItem } from '@lmc/ts-common'
import useRequiredRule from '../utils/useRequiredRule'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { ImportantContactBodyDtos } from '@lmc/ts-common/lib/dtos/Resident'

export type FamilyPortalFormType = Pick<
  ImportantContactBodyDtos,
  'familyHasAccess' | 'familyComment'
>

type FamilyPortalFormCardProps = {
  resident: ResidentItem
  editedIndex: number | undefined
  testId?: string
}

const FamilyPortalFormCard = ({
  resident,
  editedIndex,
  testId,
}: FamilyPortalFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const requiredRule = useRequiredRule()

  const importantContact = editedIndex
    ? resident.importantContacts?.[editedIndex]
    : undefined
  const familyPortalActiveSince =
    importantContact?.familyDateActivated as unknown as string

  return (
    <FormCard
      subtitle={
        familyPortalActiveSince &&
        t(supConstants.ACTIVE_SINCE_DATE, {
          date: familyPortalActiveSince,
        })
      }
      title={t(supConstants.FAMILY_PORTAL_STATUS)}
      data-testid={
        testId ? `${testId}-family-portal-form-card` : 'family-portal-form-card'
      }
    >
      <FormItem
        required
        rules={[requiredRule(t(supConstants.ACCESS))]}
        help={t(supConstants.ACCESS_HELP_MESSAGE, {
          residentName: `${resident?.name.first}`,
        })}
        label={t(supConstants.ACCESS)}
        name="familyHasAccess"
      >
        <FormRadioGroup
          data-testid={
            testId
              ? `${testId}-family-portal-access-group`
              : 'family-portal-access-group'
          }
        >
          {/* eslint-disable-next-line react/jsx-boolean-value */}
          <FormRadio
            value={true}
            text={t(supConstants.YES)}
            data-testid={
              testId
                ? `${testId}-family-portal-access-yes`
                : 'family-portal-access-yes'
            }
          />
          <FormRadio
            value={false}
            text={t(supConstants.NO)}
            data-testid={
              testId
                ? `${testId}-family-portal-access-no`
                : 'family-portal-access-no'
            }
          />
        </FormRadioGroup>
      </FormItem>
      <FormItem
        label={t(supConstants.JUSTIFICATION_OF_DECISION)}
        name="familyComment"
      >
        <TextArea
          autoSize={{
            minRows: 3,
          }}
          data-testid={
            testId ? `${testId}-family-portal-comment` : 'family-portal-comment'
          }
        />
      </FormItem>
      <LogText level={2}>
        {t(supConstants.ANY_CHANGES_WILL_AUTOMATICALLY_CREATE_A_LOG)}
      </LogText>
    </FormCard>
  )
}

const LogText = styled(Text)`
  margin-top: 24px;
  color: ${(props) => props.theme.color.neutral.color5};
`

export default FamilyPortalFormCard
